/**
 * 科目・キャラクター・アイテム・ガチャの下書き(docs/19 §3・docs/20 §5)。
 *
 * - 読み取り: Firestore `cmsDrafts`(role クレームがあれば読める)を docType ごとに読む。
 * - 書込み: **すべて `cmsSaveDrafts` callable**(管理画面からも Firestore へ直接書かない。docs/20 §1)。
 *   1件ごとに `expectedRevision`(読んだときの revision。新規は 0)を付け、1件でも合わなければ
 *   サーバーが全体を失敗させる(docs/20 §3)。
 * - 入力中の検証は `@paltyping/content-schema` の `validateCatalogDefs` を、編集中の値を差し込んだ
 *   カタログ全体に対して回す(参照・プール・入手経路の検証は全体を見ないとできないため)。
 */
import {
  DEFAULT_GACHA_ECONOMY,
  GACHA_CURRENCIES,
  RIGS,
  SPECIAL_CURRENCIES,
  validateCatalogDefs,
  type CatalogDefs,
  type CharacterDef,
  type CmsDataByType,
  type EmojiBaseDef,
  type GachaDef,
  type GachaPoolEntry,
  type ItemDef,
  type LocalizedWord,
  type Rarity,
  type RigType,
  type SubjectDef,
  type ValidationResult,
} from '@paltyping/content-schema';
import { collection, getDocs, query, where, type Firestore } from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import { toCmsError } from './cms';

export const CMS_DRAFTS_COLLECTION = 'cmsDrafts';

/** この画面で扱う下書きの種類。 */
export const CATALOG_DOC_TYPES = ['subject', 'character', 'item', 'gacha', 'retirement'] as const;
export type CatalogDocType = (typeof CATALOG_DOC_TYPES)[number];

/** 1回の保存の上限(functions の ADMIN_RULES.maxOpsPerSave と同じ)。 */
export const MAX_OPS_PER_SAVE = 500;

/** 一覧の1ページの件数(数百件でも重くしない。docs/20 §5)。 */
export const PAGE_SIZE = 50;

export interface DraftEntry<T extends CatalogDocType> {
  readonly id: string;
  /** 読んだ時点の revision(保存の expectedRevision に使う)。 */
  readonly revision: number;
  readonly data: CmsDataByType[T];
}

export type CatalogDrafts = { readonly [T in CatalogDocType]: readonly DraftEntry<T>[] };

export const EMPTY_DRAFTS: CatalogDrafts = {
  subject: [],
  character: [],
  item: [],
  gacha: [],
  retirement: [],
};

/** 保存したい中身(1ドキュメントぶん)。 */
export type CatalogChange = {
  [T in CatalogDocType]: {
    readonly docType: T;
    readonly id: string;
    readonly data: CmsDataByType[T];
  };
}[CatalogDocType];

/** `cmsSaveDrafts` の1操作(functions/src/admin/drafts.ts の SaveDraftOp)。 */
export interface SaveDraftOp {
  readonly docType: CatalogDocType;
  readonly id: string;
  readonly data: unknown;
  readonly expectedRevision: number;
}

export interface SaveDraftResult {
  readonly docType: string;
  readonly id: string;
  readonly revision: number;
  readonly deleted: boolean;
}

// ── 読み書きの口(テストでは差し替える)───────────────────

export interface CatalogDraftReader {
  load(): Promise<CatalogDrafts>;
}

export interface CatalogDraftSaver {
  save(ops: readonly SaveDraftOp[], operationId: string): Promise<readonly SaveDraftResult[]>;
}

function byOrderThenId(
  a: { readonly id: string; readonly order: number },
  b: { readonly id: string; readonly order: number },
): number {
  return a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

function orderOf(docType: CatalogDocType, data: unknown): number {
  const record = (data ?? {}) as { order?: unknown; def?: { order?: unknown } };
  const value = docType === 'subject' ? record.def?.order : record.order;
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** 下書きの並びを `order`(科目は `def.order`)→ id 順に揃える。 */
export function sortDrafts<T extends CatalogDocType>(
  docType: T,
  entries: readonly DraftEntry<T>[],
): DraftEntry<T>[] {
  return [...entries].sort((a, b) =>
    byOrderThenId(
      { id: a.id, order: orderOf(docType, a.data) },
      { id: b.id, order: orderOf(docType, b.data) },
    ),
  );
}

/** Firestore のドキュメント(`{ docType, id, data, revision }`)を画面の形へ。読めなければ null。 */
export function toDraftEntry<T extends CatalogDocType>(
  docType: T,
  raw: Record<string, unknown>,
): DraftEntry<T> | null {
  if (raw.docType !== docType || typeof raw.id !== 'string') return null;
  const data = raw.data as { def?: unknown } | undefined;
  if (typeof data !== 'object' || data === null || typeof data.def !== 'object') return null;
  const revision =
    typeof raw.revision === 'number' && Number.isInteger(raw.revision) ? raw.revision : 0;
  return { id: raw.id, revision, data: data as unknown as CmsDataByType[T] };
}

export function createCatalogDraftReader(db: Firestore): CatalogDraftReader {
  return {
    load: async () => {
      const lists = await Promise.all(
        CATALOG_DOC_TYPES.map(async (docType) => {
          const snapshot = await getDocs(
            query(collection(db, CMS_DRAFTS_COLLECTION), where('docType', '==', docType)),
          );
          const entries = snapshot.docs
            .map((entry) => toDraftEntry(docType, entry.data()))
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
          return [docType, sortDrafts(docType, entries)] as const;
        }),
      );
      return Object.fromEntries(lists) as unknown as CatalogDrafts;
    },
  };
}

type Invoke = (name: string, data: unknown) => Promise<unknown>;

export function createCatalogDraftSaverWith(invoke: Invoke): CatalogDraftSaver {
  return {
    save: async (ops, operationId) => {
      let result: unknown;
      try {
        result = await invoke('cmsSaveDrafts', { ops, operationId });
      } catch (error) {
        throw toCmsError(error);
      }
      const results = (result as { results?: unknown } | null)?.results;
      return Array.isArray(results) ? (results as SaveDraftResult[]) : [];
    },
  };
}

export function createCatalogDraftSaver(functions: Functions): CatalogDraftSaver {
  return createCatalogDraftSaverWith(
    async (name, data) => (await httpsCallable(functions, name)(data)).data,
  );
}

// ── 保存要求の組み立て ─────────────────────────────────────

export function findDraft<T extends CatalogDocType>(
  drafts: CatalogDrafts,
  docType: T,
  id: string,
): DraftEntry<T> | undefined {
  return (drafts[docType] as readonly DraftEntry<T>[]).find((entry) => entry.id === id);
}

/** 変更を `cmsSaveDrafts` の ops にする。expectedRevision は読んだ revision(新規は 0)。 */
export function buildSaveOps(
  drafts: CatalogDrafts,
  changes: readonly CatalogChange[],
): SaveDraftOp[] {
  return changes.map((change) => ({
    docType: change.docType,
    id: change.id,
    data: change.data,
    expectedRevision: findDraft(drafts, change.docType, change.id)?.revision ?? 0,
  }));
}

/** 保存に成功した変更を手元の下書きへ反映する(revision はサーバーの返した値)。 */
export function applySaveResults(
  drafts: CatalogDrafts,
  changes: readonly CatalogChange[],
  results: readonly SaveDraftResult[],
): CatalogDrafts {
  const next: Record<CatalogDocType, DraftEntry<CatalogDocType>[]> = {
    subject: [...drafts.subject],
    character: [...drafts.character],
    item: [...drafts.item],
    gacha: [...drafts.gacha],
    retirement: [...drafts.retirement],
  };
  for (const change of changes) {
    const result = results.find((r) => r.docType === change.docType && r.id === change.id);
    const list = next[change.docType];
    const index = list.findIndex((entry) => entry.id === change.id);
    const previous = index >= 0 ? list[index] : undefined;
    const revision = result?.revision ?? (previous?.revision ?? 0) + 1;
    const entry = { id: change.id, revision, data: change.data } as DraftEntry<CatalogDocType>;
    if (index >= 0) list[index] = entry;
    else list.push(entry);
  }
  return {
    subject: sortDrafts('subject', next.subject as DraftEntry<'subject'>[]),
    character: sortDrafts('character', next.character as DraftEntry<'character'>[]),
    item: sortDrafts('item', next.item as DraftEntry<'item'>[]),
    gacha: sortDrafts('gacha', next.gacha as DraftEntry<'gacha'>[]),
    retirement: sortDrafts('retirement', next.retirement as DraftEntry<'retirement'>[]),
  };
}

// ── 検証 ───────────────────────────────────────────────────

/** 編集中の値を差し込むためのもの。 */
export interface DefOverrides {
  readonly subject?: SubjectDef;
  readonly character?: CharacterDef;
  readonly items?: readonly ItemDef[];
  readonly gacha?: GachaDef;
}

function replaceById<T extends { readonly id: string }>(
  list: readonly T[],
  overrides: readonly T[],
): T[] {
  if (overrides.length === 0) return [...list];
  const byId = new Map(overrides.map((entry) => [entry.id, entry]));
  const out = list.map((entry) => byId.get(entry.id) ?? entry);
  for (const entry of overrides) {
    if (!list.some((existing) => existing.id === entry.id)) out.push(entry);
  }
  return out;
}

/** 下書き(+編集中の値)を検証用の定義一式にする。 */
export function catalogDefsFrom(drafts: CatalogDrafts, overrides: DefOverrides = {}): CatalogDefs {
  return {
    subjects: replaceById(
      drafts.subject.map((entry) => entry.data.def),
      overrides.subject ? [overrides.subject] : [],
    ),
    characters: replaceById(
      drafts.character.map((entry) => entry.data.def),
      overrides.character ? [overrides.character] : [],
    ),
    items: replaceById(
      drafts.item.map((entry) => entry.data.def),
      overrides.items ?? [],
    ),
    gachas: replaceById(
      drafts.gacha.map((entry) => entry.data.def),
      overrides.gacha ? [overrides.gacha] : [],
    ),
    retirements: drafts.retirement.map((entry) => entry.data.def),
  };
}

export function validateDrafts(
  drafts: CatalogDrafts,
  overrides: DefOverrides = {},
): ValidationResult {
  return validateCatalogDefs(catalogDefsFrom(drafts, overrides));
}

export const CATALOG_PATHS: Readonly<Record<Exclude<CatalogDocType, 'retirement'>, string>> = {
  subject: 'catalog/subjects.json',
  character: 'catalog/characters.json',
  item: 'catalog/items.json',
  gacha: 'catalog/gachas.json',
};

/**
 * 検証メッセージのうち、ある1件(科目・キャラクター・アイテム・ガチャ)に関するもの。
 * content-schema のメッセージは `catalog/items.json[3](id).field: …` か
 * `catalog/items.json: id は…` / `…: アイテム id が…` の形。
 */
export function isIssueFor(
  message: string,
  docType: Exclude<CatalogDocType, 'retirement'>,
  id: string,
): boolean {
  const path = CATALOG_PATHS[docType];
  if (message.startsWith(`${path}[`) && message.includes(`](${id})`)) return true;
  if (docType === 'item') {
    if (message.startsWith(`${path}: ${id} `)) return true;
    if (message.startsWith(`${CATALOG_PATHS.gacha}: アイテム ${id} `)) return true;
  }
  if (docType === 'character' && message.startsWith(`${path}: ${id} `)) return true;
  return false;
}

export interface EntityIssues {
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
}

export function issuesFor(
  result: ValidationResult,
  docType: Exclude<CatalogDocType, 'retirement'>,
  id: string,
): EntityIssues {
  return {
    errors: result.errors.filter((message) => isIssueFor(message, docType, id)),
    warnings: result.warnings.filter((message) => isIssueFor(message, docType, id)),
  };
}

/** 項目(`.rarityRates` `.pool` `name.ja` など)に関するものだけ。 */
export function fieldIssues(issues: EntityIssues, field: string): EntityIssues {
  const matches = (message: string) => {
    const head = message.split(': ')[0] ?? '';
    return head.includes(`.${field}`) || (field === 'name' && message.includes('name.ja'));
  };
  return {
    errors: issues.errors.filter(matches),
    warnings: issues.warnings.filter(matches),
  };
}

// ── 科目 ───────────────────────────────────────────────────

/** 新しく作れる科目ID(通貨キーのうち、特別通貨と既存の科目を除いたもの。docs/19 §2)。 */
export function availableSubjectIds(drafts: CatalogDrafts): string[] {
  const used = new Set(drafts.subject.map((entry) => entry.id));
  return GACHA_CURRENCIES.filter(
    (currency) => !SPECIAL_CURRENCIES.includes(currency) && !used.has(currency),
  );
}

export function newSubjectDef(
  drafts: CatalogDrafts,
  id: SubjectDef['currency'],
  characterId: string,
): SubjectDef {
  const maxOrder = drafts.subject.reduce((max, entry) => Math.max(max, entry.data.def.order), 0);
  return {
    id,
    name: { ja: '' },
    currency: id,
    characterId,
    color: '#888888',
    icon: '',
    order: maxOrder + 1,
    status: 'paused',
  };
}

// ── キャラクター ───────────────────────────────────────────

/**
 * 名前の部品の新しい id(`{prefix}-001` から、使ったことのある番号より大きいもの)。作った後は変えない
 * (保存済みの名前が id を指すため。docs/19 §9)。`reserved` には保存済みの id を渡し、
 * 削除した直後に同じ id を別のことばへ使い回さないようにする。
 */
export function nextWordId(
  words: readonly LocalizedWord[],
  prefix: string,
  reserved: Iterable<string> = [],
): string {
  const used = new Set([...words.map((word) => word.id), ...reserved]);
  let max = 0;
  const pattern = new RegExp(`^${prefix}-(\\d+)$`);
  for (const id of used) {
    const match = pattern.exec(id);
    if (match) max = Math.max(max, Number.parseInt(match[1] ?? '0', 10));
  }
  let n = max + 1;
  let id = `${prefix}-${String(n).padStart(3, '0')}`;
  while (used.has(id)) {
    n += 1;
    id = `${prefix}-${String(n).padStart(3, '0')}`;
  }
  return id;
}

/** その枠の starter アイテム(初期の見た目に選べるもの)。 */
export function starterItems(
  items: readonly ItemDef[],
  characterId: string,
  slotId: string,
): ItemDef[] {
  return items.filter(
    (item) =>
      item.characterId === characterId &&
      item.slot === slotId &&
      item.starter === true &&
      item.status === 'active',
  );
}

// ── アイテム ───────────────────────────────────────────────

export interface ItemFilter {
  readonly characterId: string;
  readonly slot: string;
  readonly rarity: '' | Rarity;
  readonly status: '' | ItemDef['status'];
  readonly unnamedOnly: boolean;
  readonly text: string;
}

export const EMPTY_ITEM_FILTER: ItemFilter = {
  characterId: '',
  slot: '',
  rarity: '',
  status: '',
  unnamedOnly: false,
  text: '',
};

export function isUnnamed(item: ItemDef): boolean {
  return (item.name.ja ?? '').trim() === '';
}

export function matchesItemFilter(item: ItemDef, filter: ItemFilter): boolean {
  if (filter.characterId !== '' && item.characterId !== filter.characterId) return false;
  if (filter.slot !== '' && item.slot !== filter.slot) return false;
  if (filter.rarity !== '' && item.rarity !== filter.rarity) return false;
  if (filter.status !== '' && item.status !== filter.status) return false;
  if (filter.unnamedOnly && !isUnnamed(item)) return false;
  const text = filter.text.trim().toLowerCase();
  if (text !== '') {
    const name = (item.name.ja ?? '').toLowerCase();
    if (!item.id.toLowerCase().includes(text) && !name.includes(text)) return false;
  }
  return true;
}

export function paginate<T>(list: readonly T[], page: number, size: number = PAGE_SIZE): T[] {
  const start = Math.max(0, page) * size;
  return list.slice(start, start + size);
}

export function pageCount(total: number, size: number = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / size));
}

/** アイテムID → それが入っているガチャID(一覧の「ガチャ」列)。 */
export function gachasByItem(gachas: readonly GachaDef[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const gacha of gachas) {
    for (const entry of gacha.pool) {
      const list = map.get(entry.itemId);
      if (list) list.push(gacha.id);
      else map.set(entry.itemId, [gacha.id]);
    }
  }
  return map;
}

/** 選んだアイテムのレアリティを一括で変える。変わらないものは保存しない。 */
export function bulkRarityChanges(
  items: readonly DraftEntry<'item'>[],
  ids: ReadonlySet<string>,
  rarity: Rarity,
): CatalogChange[] {
  return items
    .filter((entry) => ids.has(entry.id) && entry.data.def.rarity !== rarity)
    .map((entry) => ({
      docType: 'item' as const,
      id: entry.id,
      data: { ...entry.data, def: { ...entry.data.def, rarity } },
    }));
}

// ── ガチャのプール ─────────────────────────────────────────

export interface PoolFilter {
  readonly slot: string;
  readonly rarity: '' | Rarity;
}

function matchesPoolFilter(item: ItemDef, filter: PoolFilter): boolean {
  if (filter.slot !== '' && item.slot !== filter.slot) return false;
  if (filter.rarity !== '' && item.rarity !== filter.rarity) return false;
  return true;
}

/**
 * プールに入れてよいアイテム(同じキャラクターの equip 枠と base 枠・starter でない・有効。
 * docs/19 §3 2026-10-24 改定: starter でない素体もガチャで出す)。
 */
export function poolCandidates(
  gacha: GachaDef,
  characters: readonly CharacterDef[],
  items: readonly ItemDef[],
  filter: PoolFilter,
): ItemDef[] {
  const character = characters.find((entry) => entry.id === gacha.characterId);
  const slots = new Set((character?.slots ?? []).map((slot) => slot.id));
  const inPool = new Set(gacha.pool.map((entry) => entry.itemId));
  return items.filter(
    (item) =>
      item.characterId === gacha.characterId &&
      slots.has(item.slot) &&
      item.starter !== true &&
      item.status === 'active' &&
      !inPool.has(item.id) &&
      matchesPoolFilter(item, filter),
  );
}

/** 条件に合う候補をまとめてプールへ足す。 */
export function addPoolEntries(
  gacha: GachaDef,
  characters: readonly CharacterDef[],
  items: readonly ItemDef[],
  filter: PoolFilter,
  weight: number,
): GachaDef {
  const added: GachaPoolEntry[] = poolCandidates(gacha, characters, items, filter).map((item) => ({
    itemId: item.id,
    weight,
  }));
  return { ...gacha, pool: [...gacha.pool, ...added] };
}

function poolEntryMatches(
  entry: GachaPoolEntry,
  itemsById: ReadonlyMap<string, ItemDef>,
  filter: PoolFilter,
): boolean {
  const item = itemsById.get(entry.itemId);
  // カタログに無いもの(壊れた参照)は、絞り込みなしのときだけ対象にする。
  if (!item) return filter.slot === '' && filter.rarity === '';
  return matchesPoolFilter(item, filter);
}

/** 条件に合う枠をまとめて外す。 */
export function removePoolEntries(
  gacha: GachaDef,
  itemsById: ReadonlyMap<string, ItemDef>,
  filter: PoolFilter,
): GachaDef {
  return {
    ...gacha,
    pool: gacha.pool.filter((entry) => !poolEntryMatches(entry, itemsById, filter)),
  };
}

/** 条件に合う枠の weight をまとめて変える。 */
export function setPoolWeights(
  gacha: GachaDef,
  itemsById: ReadonlyMap<string, ItemDef>,
  filter: PoolFilter,
  weight: number,
): GachaDef {
  return {
    ...gacha,
    pool: gacha.pool.map((entry) =>
      poolEntryMatches(entry, itemsById, filter) ? { ...entry, weight } : entry,
    ),
  };
}

export function rateSum(rates: Readonly<Record<Rarity, number>>): number {
  return Object.values(rates).reduce((sum, rate) => sum + (Number.isFinite(rate) ? rate : 0), 0);
}

// ── 新規作成(キャラクター・ガチャ)─────────────────────────

/** キャラクター・ガチャの ID(小文字ケバブ。docs/19 §2)。 */
export const KEBAB_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** 新しい ID の問題点(なければ null)。 */
export function newIdProblem(
  id: string,
  existing: readonly { readonly id: string }[],
): string | null {
  if (id === '') return 'ID を入力してください';
  if (!KEBAB_ID_PATTERN.test(id))
    return 'ID は小文字の英数字とハイフン(例: math-hero-02)にしてください';
  if (existing.some((entry) => entry.id === id)) return `ID ${id} はもう使われています`;
  return null;
}

/** 次の並び順(`order` の最大 + 1)。 */
export function nextOrder(entries: readonly DraftEntry<'character' | 'item' | 'gacha'>[]): number {
  return entries.reduce((max, entry) => Math.max(max, entry.data.order), -1) + 1;
}

/** 新しいキャラクター。枠はリグの既定、休止、初期の見た目・名前の部品は空。 */
export function newCharacterDef(
  id: string,
  name: string,
  rigType: RigType,
  /** legacy-emoji の素体(既存の legacy-emoji キャラクターから引き継ぐ。無ければ空 = 検証エラー)。 */
  bases: readonly EmojiBaseDef[] = [],
): CharacterDef {
  const rig = RIGS[rigType];
  return {
    id,
    name: { ja: name },
    rigType,
    slots: rig.slots,
    defaultLook: { equipped: {} },
    nameParts: { prefixes: [], nouns: [] },
    ...(rig.usesBases ? { bases: bases.map((base) => ({ ...base })) } : {}),
    status: 'paused',
  };
}

/** ガチャの標準値は content-schema の1か所(functions の GACHA_RULES との一致はパリティテストで保証)。 */
export { DEFAULT_GACHA_ECONOMY };

/** 新しいガチャ。通貨・キャラクターは科目のもの、休止、プールは空、期間は無期限。 */
export function newGachaDef(id: string, name: string, subject: SubjectDef): GachaDef {
  return {
    id,
    name: { ja: name },
    currency: subject.currency,
    cost: DEFAULT_GACHA_ECONOMY.cost,
    cost10: DEFAULT_GACHA_ECONOMY.cost10,
    pool: [],
    period: { from: null, to: null },
    subjectId: subject.id,
    characterId: subject.characterId,
    rarityRates: { ...DEFAULT_GACHA_ECONOMY.rarityRates },
    guarantee: { ...DEFAULT_GACHA_ECONOMY.guarantee },
    duplicateConvertCoins: { ...DEFAULT_GACHA_ECONOMY.duplicateConvertCoins },
    status: 'paused',
  };
}

/**
 * 既存のガチャを複製する(価格・排出率・保証・重複変換・プール・科目・キャラクターを引き継ぐ)。
 * 休止で作り、期間は無期限に戻す(同じアイテムが2本の有効なガチャに入らないよう、有効にする前に見直す)。
 */
export function duplicateGachaDef(source: GachaDef, id: string, name: string): GachaDef {
  return {
    ...source,
    id,
    name: { ...source.name, ja: name },
    pool: source.pool.map((entry) => ({ ...entry })),
    period: { from: null, to: null },
    status: 'paused',
  };
}
